/**
 * PlanFamilyCard — card de plan (En Vivo / Premium) compartido entre /pricing
 * y la landing (Sprint 10.24 F3b).
 *
 * Planes nuevos (Marco, 2026-10-24): Free con toda la biblioteca, En Vivo
 * (código interno `pro`, USD 99) con todos los cursos en vivo, y Premium
 * (USD 199) = En Vivo + 1 sesión 1:1 al mes. Debajo del precio va el ancla:
 * lo que costaría comprar suelto lo que el plan incluye.
 *
 * Mantiene exactamente el mismo diseño y lógica de pricing dinámico Founder:
 *   - Si hay cupos Founder → muestra precio Founder + badge + ahorro
 *   - Si NO hay cupos → muestra precio público sin badge
 *
 * Extraído de PricingPage para que la landing muestre exactamente lo mismo
 * sin duplicar copy ni features.
 */
import { Check, Crown, Sparkles, Gem } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, cn } from '@/lib/utils';
import type { FounderSeatStatus } from '@/lib/api/subscription';

export type Family = 'pro' | 'premium';

export interface FamilyDescriptor {
  family: Family;
  displayName: string;
  tagline: string;
  founderCode: 'pro_founder' | 'premium_founder';
  founderPrice: number;
  publicPrice: number;
  features: string[];
  /** Ancla de precio bajo el precio: lo que cuesta suelto (planes nuevos). */
  anchor?: string;
  recommended?: boolean;
}

/** Catálogo canónico de planes — la única fuente de verdad para landing y pricing. */
export const FAMILIES: FamilyDescriptor[] = [
  {
    family: 'pro',
    displayName: 'En Vivo',
    tagline: 'Por el precio de un curso, todos los del mes',
    founderCode: 'pro_founder',
    founderPrice: 1900,
    publicPrice: 9900,
    features: [
      'Todos los cursos en vivo con CEOs, directores y fundadores',
      'La grabación de cada curso durante 7 días',
      'Toda la biblioteca de cursos, con certificado',
      'Comunidad privada con gurus y miembros',
      'Todos los eventos de miembros',
      'Puntos canjeables por cupones en sesiones 1:1',
    ],
    anchor: 'Una entrada suelta cuesta USD 59 a 79',
    recommended: true,
  },
  {
    family: 'premium',
    displayName: 'Premium',
    tagline: 'Todo En Vivo + tu sesión 1:1 del mes',
    founderCode: 'premium_founder',
    founderPrice: 14900,
    // Post-beta calibrado a USD 199 (en lugar de USD 249): sostiene margen
    // con techo Guru USD 200.
    publicPrice: 19900,
    features: [
      'Todo lo de En Vivo',
      '1 sesión 1:1 al mes con el guru que elijas',
      'Hasta USD 272/año en cupones canjeables',
    ],
    anchor: 'Una sesión suelta cuesta desde USD 150',
  },
];

/**
 * Identidad de color por tier — FUENTE ÚNICA DE VERDAD.
 * La usan: el badge del header (DashboardLayout), /pricing (esta card) y la
 * comparación de Mi Cuenta. PLAN_TIER_STYLE = acentos completos (ring/border/
 * text/glow/tint); PLAN_TIER_COLOR = solo la clase bg (dot/badge/tile), derivado.
 * Si agregás un tier (Free), defines su estilo acá, en un solo lugar.
 */
export const PLAN_TIER_STYLE: Record<Family, {
  bg: string; ring: string; border: string; text: string;
  glow: string; tintBg: string; tintBorder: string; gradFrom: string; gradVia: string;
}> = {
  pro: {
    bg: 'bg-brand-accent', ring: 'ring-brand-accent', border: 'border-brand-accent', text: 'text-brand-accent',
    glow: 'shadow-[0_0_40px_-10px_rgba(42,163,185,0.5)]',
    tintBg: 'bg-brand-accent/[0.05]', tintBorder: 'border-brand-accent/30',
    gradFrom: 'from-brand-accent/20', gradVia: 'via-brand-accent',
  },
  premium: {
    bg: 'bg-brand-green', ring: 'ring-brand-green', border: 'border-brand-green', text: 'text-brand-green',
    glow: 'shadow-[0_0_40px_-10px_rgba(100,166,113,0.5)]',
    tintBg: 'bg-brand-green/[0.05]', tintBorder: 'border-brand-green/30',
    gradFrom: 'from-brand-green/20', gradVia: 'via-brand-green',
  },
};

/** Solo la clase bg por tier (dot/badge/tile) — derivado de PLAN_TIER_STYLE. */
export const PLAN_TIER_COLOR: Record<Family, string> = {
  pro: PLAN_TIER_STYLE.pro.bg,
  premium: PLAN_TIER_STYLE.premium.bg,
};

export function PlanFamilyCard({
  descriptor,
  seats,
  onSubscribe,
  loading,
  currentFamily = null,
  promo,
}: {
  descriptor: FamilyDescriptor;
  seats?: FounderSeatStatus;
  onSubscribe: () => void;
  loading: boolean;
  /** Familia del plan activo del usuario (si tiene). null = sin plan / anónimo. */
  currentFamily?: Family | null;
  /** Freemium: precio de lanzamiento (cupón que el checkout aplica solo). */
  promo?: { priceCents: number; code: string; months: number | null; endsLabel: string } | null;
}) {
  const isCurrent = currentFamily === descriptor.family;
  const hasOtherPlan = !!currentFamily && !isCurrent;
  const available = seats?.available_seats ?? 0;
  const maxSeats = seats?.max_seats ?? 0;
  const takenSeats = seats?.taken_seats ?? 0;
  const founderActive = available > 0;
  // El cupón de lanzamiento aplica sobre el precio público (sin cupo Founder).
  const promoActive = !founderActive && !!promo && !currentFamily;
  const activePrice = founderActive
    ? descriptor.founderPrice
    : promoActive ? promo!.priceCents : descriptor.publicPrice;
  const savings = descriptor.publicPrice - descriptor.founderPrice;
  const pctTaken = maxSeats > 0 ? Math.round((takenSeats / maxSeats) * 100) : 0;
  // Estilo de identidad del tier (acentos de la card siguen el color del producto).
  const t = PLAN_TIER_STYLE[descriptor.family];

  return (
    <div
      className={cn(
        // p-6 en movil: con p-8 + container la card quedaba a ~300px utiles y
        // el CTA Founder (whitespace-nowrap del Button) la desbordaba.
        'relative flex flex-col gap-4 rounded-2xl p-6 transition-all sm:p-8',
        descriptor.recommended
          ? // Recomendado: borde + anillo + glow en el COLOR DEL TIER (no fijo cian).
            cn('card-dark bg-gradient-to-br to-transparent ring-2 md:scale-105', t.border, t.gradFrom, t.ring, t.glow)
          : 'card-dark-hover'
      )}
    >
      {/* Glow decorativo arriba (solo recomendado) */}
      {descriptor.recommended && (
        <div
          aria-hidden
          className={cn('pointer-events-none absolute -top-px left-1/2 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent to-transparent', t.gradVia)}
        />
      )}

      {descriptor.recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className={cn('text-white border-transparent shadow-lg', t.bg)}>
            <Gem className="h-3 w-3" /> Mejor valor
          </Badge>
        </div>
      )}

      <div>
        <div className="flex items-center gap-2">
          {/* Chip de identidad de tier — mismo color que el badge del header. */}
          <span
            aria-hidden
            className={cn('h-2.5 w-2.5 flex-shrink-0 rounded-full', PLAN_TIER_COLOR[descriptor.family])}
          />
          {descriptor.recommended && (
            <Crown className={cn('h-5 w-5', t.text)} />
          )}
          <h3 className={cn(
            'font-semibold text-white',
            descriptor.recommended ? 'text-xl' : 'text-lg'
          )}>
            {descriptor.displayName}
          </h3>
          {founderActive && (
            <Badge className="bg-white/10 text-brand-text-dim border-white/15">
              <Sparkles className="h-3 w-3" /> Founder
            </Badge>
          )}
          {/* Sin sello "Lanzamiento" junto al nombre (Marco, 2026-10-25): la
              franja de arriba y el precio tachado ya lo dicen. */}
          {isCurrent && (
            <Badge className={cn('border-transparent text-white', t.bg)}>Tu plan</Badge>
          )}
        </div>
        <p className="mt-1 text-xs text-brand-text-dim">{descriptor.tagline}</p>
      </div>

      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold text-white">{formatCurrency(activePrice)}</span>
          <span className="text-sm text-brand-text-dim">/mes</span>
          {(founderActive || promoActive) && (
            <span className="ml-2 text-base text-brand-text-dim/70 line-through">
              {formatCurrency(descriptor.publicPrice)}
            </span>
          )}
        </div>
        {promoActive && (
          <p className={cn('mt-1 text-xs font-medium', t.text)}>
            {promo!.months == null
              ? `Para siempre, si entras antes del ${promo!.endsLabel}`
              : `Precio de lanzamiento los primeros ${promo!.months} meses · luego ${formatCurrency(descriptor.publicPrice)}/mes`}
          </p>
        )}
        {descriptor.anchor && (
          <p className="mt-1 text-xs text-brand-text-dim">{descriptor.anchor}</p>
        )}
        {founderActive && (
          <p className={cn('mt-1 text-xs font-medium', t.text)}>
            🔒 Lock-in vitalicio · Ahorras {formatCurrency(savings)} /mes
          </p>
        )}
      </div>

      {/* Cupo Founder — sólo si hay cupos */}
      {founderActive && (
        <div className={cn('rounded-lg border p-3', t.tintBorder, t.tintBg)}>
          <div className="flex items-baseline justify-between text-xs">
            <span className="uppercase tracking-wider text-brand-text-dim">Cupos Founder restantes</span>
            <span className="font-mono text-white">
              <strong className="text-base">{available}</strong> / {maxSeats}
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className={cn(
                'h-full transition-all',
                pctTaken > 80 ? 'bg-red-500' : t.bg
              )}
              style={{ width: `${pctTaken}%` }}
            />
          </div>
          {takenSeats > 0 && (
            <p className="mt-1.5 text-[10px] text-brand-text-dim/80">
              {takenSeats} founders ya se unieron · cuando se agote, sube a {formatCurrency(descriptor.publicPrice)}
            </p>
          )}
        </div>
      )}

      <ul className="flex-1 space-y-2.5 border-t border-white/10 pt-5 text-sm">
        {descriptor.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-white">
            <Check className={cn('h-4 w-4 mt-0.5 flex-shrink-0', t.text)} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <Button variant="on-dark" size="lg" className="h-auto min-h-11 w-full whitespace-normal py-3" disabled>
          Tu plan actual
        </Button>
      ) : (
        <Button
          variant="on-dark"
          size="lg"
          // whitespace-normal + h-auto: el texto Founder es largo y el Button
          // trae whitespace-nowrap de fabrica; en movil debe poder envolver
          // en 2 lineas en vez de ensanchar la card (desborde horizontal).
          className="h-auto min-h-11 w-full whitespace-normal py-3"
          onClick={onSubscribe}
          loading={loading}
        >
          {hasOtherPlan
            ? 'Gestionar mi plan'
            : founderActive
              ? `Quiero el precio Founder · ${formatCurrency(descriptor.founderPrice)}/mes`
              : `Suscribirme a ${descriptor.displayName}`}
        </Button>
      )}
      <p className="text-center text-[11px] text-brand-text-dim/70">
        {isCurrent
          ? 'Gestiona tu plan desde Mi Cuenta.'
          : hasOtherPlan
            ? 'Cambia o gestiona tu plan desde Mi Cuenta.'
            : promoActive
              ? `Código ${promo!.code} aplicado al pagar · Hasta el ${promo!.endsLabel}`
              : 'Pago seguro vía Stripe · Cancela cuando quieras'}
      </p>
    </div>
  );
}

/**
 * FreePlanCard — la tarjeta del plan Free (freemium, 29-sep-2026).
 *
 * Aparece a la izquierda de En Vivo y Premium solo cuando la beta está cerrada.
 * El Free trae 15 cursos completos (Marco, 2026-10-25); la biblioteca entera
 * viene con En Vivo.
 * El número de cursos sale de la base (useFreeCourseCount): si el admin
 * marca más o menos cursos como Free, la tarjeta se actualiza sola.
 */
export function FreePlanCard({
  freeCourseCount,
  ctaLabel,
  onClick,
  disabled = false,
  isCurrent = false,
}: {
  freeCourseCount?: number;
  ctaLabel: string;
  onClick: () => void;
  disabled?: boolean;
  isCurrent?: boolean;
}) {
  const features = [
    freeCourseCount && freeCourseCount > 0
      ? `${freeCourseCount} cursos completos de la biblioteca, con certificado`
      : 'Cursos completos de la biblioteca, con certificado',
    'Podcasts con gurus e invitados',
    'Explora todo el catálogo de cursos y gurus',
    'Reserva sesiones 1:1 con gurus: pagas solo la sesión',
  ];
  return (
    <div className="card-dark-hover relative flex flex-col gap-4 rounded-2xl p-6 transition-all sm:p-8">
      <div>
        <div className="flex items-center gap-2">
          <span aria-hidden className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-white/60" />
          <h3 className="text-lg font-semibold text-white">Free</h3>
          {isCurrent && (
            <Badge className="border-transparent bg-white/20 text-white">Tu plan</Badge>
          )}
        </div>
        <p className="mt-1 text-xs text-brand-text-dim">Empieza sin pagar y conoce el club</p>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-bold text-white">{formatCurrency(0)}</span>
        <span className="text-sm text-brand-text-dim">/siempre</span>
      </div>

      <ul className="flex-1 space-y-2.5 border-t border-white/10 pt-5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-white">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-white/70" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        variant="on-dark-outline"
        size="lg"
        className="h-auto min-h-11 w-full whitespace-normal py-3"
        onClick={onClick}
        disabled={disabled}
      >
        {ctaLabel}
      </Button>
      <p className="text-center text-[11px] text-brand-text-dim/70">Sin tarjeta · Mejora tu plan cuando quieras</p>
    </div>
  );
}

/**
 * Barra del cupón de lanzamiento (freemium). Va arriba de /pricing cuando ya
 * no hay cupos Founder. El descuento se aplica solo en el checkout.
 */
export function LaunchPromoBar({
  proLabel,
  premiumLabel,
  premiumMonths,
  endsLabel,
}: {
  proLabel: string;
  premiumLabel: string;
  premiumMonths: number | null;
  endsLabel: string;
}) {
  return (
    <div className="border-b border-brand-accent/30 bg-gradient-to-r from-brand-primary/40 via-brand-accent/15 to-brand-primary/40">
      <div className="container flex flex-wrap items-center justify-center gap-x-2 gap-y-1 py-3 text-center text-sm">
        <Sparkles className="h-4 w-4 text-brand-accent" />
        <span className="text-white">
          <strong>Lanzamiento:</strong> En Vivo a {proLabel}/mes para siempre y Premium a {premiumLabel}/mes{premiumMonths ? ` los primeros ${premiumMonths} meses` : ''}.
        </span>
        <span className="text-white/60">Se aplica solo al suscribirte · Si entras antes del {endsLabel}.</span>
      </div>
    </div>
  );
}

/** Banner que va arriba del grid cuando hay cupos Founder disponibles. */
export function FounderBanner({ totalAvailable }: { totalAvailable: number }) {
  if (totalAvailable <= 0) return null;
  return (
    <div className="border-b border-brand-energy/30 bg-gradient-to-r from-brand-energy/15 via-brand-energy/10 to-brand-accent/10">
      <div className="container flex flex-wrap items-center justify-center gap-2 py-3 text-sm">
        <Crown className="h-4 w-4 text-brand-energy" />
        <span className="text-white">
          <strong>Acceso Founder disponible.</strong> {totalAvailable} cupos a precio
          fundador con lock-in vitalicio.
        </span>
      </div>
    </div>
  );
}

// Alias retrocompatible — antes el componente se llamaba FounderBetaBanner.
// Se mantiene exportado para no romper imports que aún no migramos.
export const FounderBetaBanner = FounderBanner;
